<template>
  <div id="missions">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="missions-container">
          <div class="route-container mb-4 mission-heading">
            <h1>
              <v-icon class="appguide-icon">missions_icon</v-icon>
              {{ $t('missions.pageTitle') }}
            </h1>
          </div>
          <div class="mb-7 mt-12">
            <v-btn
              v-if="!this.openMissionForm"
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold"
              @click="addNewHandler"
              >{{ $t('missions.createNewSequence') }}</v-btn
            >
            <div v-else class="close-link" @click="closebuilder">
              <v-icon>arrowleft</v-icon>
              <span class="close-link-text">{{ $t('missions.close') }}</span>
            </div>
          </div>
          <v-form v-if="openMissionForm" class="mb-12" ref="addeditform">
            <v-card class="py-10 px-15">
              <v-card-title class="px-0 font-weight-bold font-size-32">{{
                editingMission
                  ? $t('missions.editMission')
                  : $t('missions.missionBuilder')
              }}</v-card-title>
              <div
                class="text-caption text-uppercase"
                :class="{
                  'error white--text font-weight-bold': haveMission.length > 0,
                }"
              >
                {{ $t('missions.totalWeeks') }}: {{ totalWeeks }}
              </div>
              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('missions.missionTitle') }}</v-card-text
              >
              <v-text-field
                v-model="missionTitle"
                variant="outlined"
                :placeholder="$t('missions.missionTitlePlaceholder')"
                :rules="[rules.required, rules.length(2)]"
                class="user-input mt-1"
                color="#A0C548"
                :loading="isediting"
                style="min-width: 100%"
              ></v-text-field>
              <v-divider class="mt-4 mb-5"></v-divider>

              <!-- ===== LANGUAGE SELECTION (NEW) ===== -->
              <v-card-text
                class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                >{{ $t('missions.requiredLanguage') }}</v-card-text
              >
              <v-alert
                type="info"
                density="compact"
                variant="outlined"
                class="mt-2 mb-4"
              >
                <div
                  class="text-caption"
                  v-html="
                    $t('missions.languageInfo', {
                      language: requiredLanguage
                        ? availableLanguages.find(
                            (l) => l.code === requiredLanguage
                          )?.label
                        : $t('missions.selectedLanguage'),
                    })
                  "
                ></div>
              </v-alert>

              <v-radio-group v-model="requiredLanguage" inline class="mt-2">
                <v-radio
                  v-for="lang in availableLanguages"
                  :key="lang.code"
                  :label="lang.label"
                  :value="lang.code"
                  color="primary"
                ></v-radio>
              </v-radio-group>

              <v-divider class="mt-2 mb-5"></v-divider>

              <!-- ===== WEEKS SELECTION (UPDATED TO USE FILTERED LIST) ===== -->
              <v-card flat class="py-5" v-if="requiredLanguage">
                <v-layout wrap class="mb-5">
                  <div
                    class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
                  >
                    {{ $t('missions.selectYourWeeks') }}
                    <span v-if="filteredWeeksByLanguage.length > 0">
                      {{
                        $t('missions.weeksAvailable', {
                          count: filteredWeeksByLanguage.length,
                        })
                      }}
                    </span>
                  </div>
                  <v-spacer></v-spacer>
                  <div
                    class="search-container"
                    v-if="filteredWeeksByLanguage.length > 0"
                  >
                    <v-text-field
                      v-model="search"
                      color="#E8E9EC"
                      class="search-field"
                      single-line
                      hide-details
                      variant="outlined"
                      flat
                      clearable
                      density="compact"
                      :label="$t('missions.search')"
                      :append-inner-icon="search ? '' : 'mdi-magnify'"
                    ></v-text-field>
                  </div>
                </v-layout>

                <!-- No Weeks Available Message -->
                <v-alert
                  v-if="filteredWeeksByLanguage.length === 0"
                  type="warning"
                  variant="outlined"
                  class="mb-4"
                >
                  <div class="text-subtitle-2 font-weight-bold mb-2">
                    {{
                      $t('missions.noWeeksAvailable', {
                        language: availableLanguages.find(
                          (l) => l.code === requiredLanguage
                        )?.label,
                      })
                    }}
                  </div>
                  <div class="text-caption">
                    {{
                      $t('missions.translateWeeksFirst', {
                        language: availableLanguages.find(
                          (l) => l.code === requiredLanguage
                        )?.label,
                      })
                    }}
                  </div>
                  <v-btn
                    size="small"
                    color="primary"
                    variant="outlined"
                    class="mt-3"
                    :to="{ name: 'Weeks' }"
                  >
                    {{ $t('missions.goToWeeksBuilder') }}
                  </v-btn>
                </v-alert>

                <!-- Weeks Grid -->
                <v-row class="" v-else>
                  <v-col
                    v-for="week in filteredWeeksByLanguage"
                    :key="week.id"
                    cols="12"
                    sm="6"
                    md="4"
                    class="py-0"
                  >
                    <v-checkbox
                      class="mt-0 pa-0 custom-selected-weeks"
                      multiple
                      false-icon="mdi-radiobox-blank"
                      true-icon="mdi-radiobox-marked"
                      :id="week.id"
                      v-model="selectedWeekIds"
                      :value="week.id"
                      :color="$vuetify.theme.dark ? '#ffffff' : '#000000'"
                      density="compact"
                      :disabled="week.disabled || week.tempdisabled"
                      @update:model-value="syncSelectedWeeks"
                    >
                      <template #label>
                        <div class="d-flex align-center">
                          <span>{{ getWeekTitle(week, requiredLanguage) }}</span>
                          <v-chip
                            v-for="lang in getAvailableLanguagesForWeek(week)"
                            :key="lang.code"
                            size="x-small"
                            :color="getLanguageColor(lang.code)"
                            variant="outlined"
                            class="ml-2"
                          >
                            {{ lang.code.toUpperCase() }}
                          </v-chip>
                        </div>
                      </template>
                    </v-checkbox>
                  </v-col>
                </v-row>
              </v-card>

              <!-- Message if no language selected yet -->
              <v-alert v-else type="info" variant="outlined" class="mt-4">
                <div class="text-subtitle-2 font-weight-bold">
                  {{ $t('missions.pleaseSelectLanguage') }}
                </div>
                <div class="text-caption">
                  {{ $t('missions.chooseLanguagePrompt') }}
                </div>
              </v-alert>
              <div v-if="selectedWeeks.length" class="">
                <v-divider class="mb-6"></v-divider>
                <div
                  class="pa-0 pt-2 mb-6 text-subtitle-1 font-weight-bold text-uppercase"
                >
                  {{ $t('missions.orderYourWeeks') }}
                </div>
              </div>
              <draggable
                v-model="selectedWeeks"
                item-key="id"
                handle=".handle"
                group="people"
                @start="drag = true"
                @end="drag = false"
              >
                <template #item="{ element }">
                  <v-list
                    rounded
                    border
                    class="border-grey mb-5 py-0"
                    :class="element.id"
                    :key="element.id"
                  >
                    <v-list-item class="selected-weeks">
                      <template #prepend>
                        <v-icon class="handle">drag_icon</v-icon>
                      </template>

                      <v-list-item-title
                        class="text-uppercase text-subtitle-2 font-weight-bold"
                      >
                        {{ getWeekTitle(element, requiredLanguage) }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </template>
              </draggable>
            </v-card>
            <div class="text-center">
              <v-tooltip location="bottom" :disabled="haveMission.length == 0">
                <template #activator="{ props }">
                  <span v-bind="props">
                    <v-btn
                      :disabled="
                        selectedWeeks.length == 0 || haveMission.length > 0
                      "
                      color="#592892"
                      class="elevation-0 font-weight-bold my-14 px-14"
                      @click="editingMission ? editMission() : addMission()"
                      >{{
                        editingMission
                          ? $t('missions.saveMission')
                          : $t('missions.addMission')
                      }}</v-btn
                    >
                  </span>
                </template>
                <span>{{ $t('missions.tooltipDuplicateWeeks') }}</span>
              </v-tooltip>
              <v-btn
                color="rgba(165, 216, 103, 1)"
                class="elevation-0 font-weight-bold my-14 ml-7 px-14"
                @click="previewMission()"
                >{{ $t('missions.previewMission') }}
                <template #loader>
                  <span>Loading...</span>
                  <span class="custom-loader">
                    <v-icon>cached</v-icon>
                  </span>
                </template>
              </v-btn>
            </div>
            <v-progress-linear
              :indeterminate="iseditingMission"
              :active="iseditingMission"
              color="#592892"
            ></v-progress-linear>
            <v-alert
              v-if="feedback"
              border="start"
              color="#592892"
              class="text-center"
            >
              {{ feedback }}
            </v-alert>
            <v-divider class="dashed"></v-divider>
          </v-form>
          <v-data-table
            :headers="adminActions"
            fixed-header
            :loading="isloading"
            :loading-text="$t('missions.loadingMissions')"
            :no-data-text="$t('missions.noMissionsFound')"
            :items="missions"
            :items-per-page="20"
            :header-props="{ sortIcon: '$sortarrowdown' }"
            :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50] }"
            item-key="id"
            :search="search"
            class="elevation-1 users-table"
          >
            <template #progress>
              <v-progress-linear
                color="#592892"
                indeterminate
              ></v-progress-linear>
            </template>

            <template v-if="isAuthenticated" #[`item.type`]="{ item }">
              <v-chip
                v-if="
                  item.cid == churchid ||
                  checkAuthorization(['superAdmin', 'admin']).length > 0
                "
                class="px-7 text-black"
                color="rgba(165, 216, 103, .32)"
                variant="flat"
              >
                {{ $helpers.uppercase(item.type) }}
              </v-chip>
            </template>
            <template #[`item.requiredLanguage`]="{ item }">
              <v-chip
                size="small"
                :color="getLanguageColor(item.requiredLanguage || 'en')"
                variant="outlined"
              >
                {{ (item.requiredLanguage || 'en').toUpperCase() }}
              </v-chip>
            </template>
            <template #[`item.uis`]="{ item }">
              <div v-if="item.weeks.length">
                {{ item.weeks.length }}
              </div>
              <div v-else>
                <span
                  v-if="!isHelpDesk()"
                  class="add-weeks-link"
                  @click="loadMissionToEdit(item)"
                  >{{ $t('missions.addWeeks') }}</span
                >
              </div>
            </template>
            <template v-if="isAuthenticated" #[`item.actions`]="{ item }">
              <div class="action-icons">
                <v-icon
                  v-if="
                    item.cid == churchid ||
                    checkAuthorization(['superAdmin', 'admin']).length > 0
                  "
                  @click.native="previewMission(item)"
                  class="mr-6"
                >
                  eye
                </v-icon>
                <v-icon
                  v-if="
                    !isHelpDesk() &&
                    (item.cid == churchid ||
                      checkAuthorization(['superAdmin']).length > 0)
                  "
                  @click.native="loadMissionToEdit(item)"
                  class="mr-6"
                  >edit</v-icon
                >
                <v-icon
                  v-if="
                    (item.cid == churchid ||
                      checkAuthorization(['superAdmin']).length > 0) &&
                    !isHelpDesk()
                  "
                  @click.native="deleteItem(item, 'missions')"
                  >delete_icon</v-icon
                >
              </div>
            </template>
          </v-data-table>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { mapGetters } from 'vuex'
  import draggable from 'vuedraggable'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import { useLanguages } from '@/helpers/useLanguages'
  import { getWeekTitle } from '@/helpers/weekTitle'
  import { openPreview } from '@/helpers/previewBridge'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'MissionsBuilder',
    setup() {
      const { enabledLangs, defaultLang } = useLanguages()
      return { enabledLangs, defaultLang }
    },
    data() {
      return {
        feedback: null,
        backdoor: 0,
        missions: [],
        missionTitle: '',
        missionCategory: [],
        allCategories: [],
        newCategory: [],
        weeks: [],
        expanded: [],
        openMissionForm: false,
        singleExpand: true,
        isloading: true,
        isFormValid: false,
        isediting: false,
        activeindex: null,
        search: '',
        searchUsers: '',
        filtered: '',
        leader: '',
        tempdisabled: false,
        editMembersDialog: false,
        editingMission: false,
        iseditingMission: false,
        missionToEdit: null,
        selectedWeekIds: [],
        selectedWeeks: [],
        requiredLanguage: null, // NEW: Selected language filter
        headers: [
          { title: 'Sequence', sortable: true, key: 'title' },
          { title: 'Weeks In Sequence', sortable: false, key: 'uis' },
          {
            title: 'Actions',
            sortable: false,
            key: 'actions',
            align: 'end',
          }, //data-table-expand
        ],
        rules: {
          length: (len) => (v) =>
            (v || '').length >= len || `min ${len} characters required `,
          required: (v) => !!v || 'This field is required',
        },
        filters: [
          'A',
          'B',
          'C',
          'D',
          'E',
          'F',
          'G',
          'H',
          'I',
          'J',
          'K',
          'L',
          'M',
          'N',
          'O',
          'P',
          'Q',
          'R',
          'S',
          'T',
          'U',
          'V',
          'W',
          'X',
          'Y',
          'Z',
        ],
      }
    },
    components: {
      draggable,
      HowToUseThisPage,
    },
    computed: {
      churchid() {
        return this.$store.getters['AppData/cid']
      },
      ...mapGetters('UserData', ['isAuthenticated']),
      adminActions() {
        let h = [
          {
            title: this.$t('missions.tableHeaders.sequence'),
            sortable: true,
            key: 'title',
          },
          {
            title: this.$t('missions.tableHeaders.cid'),
            sortable: true,
            key: 'cid',
          },
          {
            title: this.$t('missions.tableHeaders.type'),
            sortable: true,
            key: 'type',
          },
          {
            title: this.$t('missions.tableHeaders.language'),
            sortable: true,
            key: 'requiredLanguage',
          },
          {
            title: this.$t('missions.tableHeaders.weeks'),
            sortable: false,
            key: 'uis',
          },
          {
            title: this.$t('missions.tableHeaders.actions'),
            sortable: false,
            key: 'actions',
            align: 'end',
          },
        ]
        if (this.isAuthenticated) {
          if (this.checkAuthorization(['admin']).length > 0)
            h = [
              { title: 'Sequence', sortable: true, key: 'title' },
              { title: 'Type', sortable: true, key: 'type' },
              { title: 'Language', sortable: true, key: 'requiredLanguage' }, // FIXED: Changed to match field name
              { title: '#Weeks', sortable: false, key: 'uis' },
              {
                title: 'Actions',
                sortable: false,
                key: 'actions',
                align: 'end',
              },
            ]
        }
        return h
      },
      missionList() {
        return this.missions
      },
      weeksList() {
        // this backdoor is used to allow a method to recompute this property
        this.backdoor

        return this.weeks.filter((item) => {
          if (!item.title) return false
          if (!item.status) return true
          return item.status === 'published'
        })
      },
      filteredItems() {
        return this.weeksList.filter((item) => {
          if (this.search) {
            return item.title.toLowerCase().match(this.search)
          } else {
            return item.title.toLowerCase().match(this.filtered)
          }
        })
      },
      totalWeeks() {
        return this.selectedWeeks.length
      },
      haveMission() {
        // console.log('1 ', this.editingMission, this.churchid)
        // const hasMission = this.missions.filter(item => item.weeks.length === this.totalWeeks)
        const hasMission = this.missions.filter((item) => {
          if (this.editingMission) {
            if (this.churchid) {
              return (
                item.weeks.length === this.totalWeeks &&
                item.cid === this.churchid &&
                item.id !== this.missionToEdit.id
              )
            } else
              return (
                item.weeks.length === this.totalWeeks &&
                item.id !== this.missionToEdit.id
              )
          } else if (this.churchid)
            return (
              item.weeks.length === this.totalWeeks &&
              item.cid &&
              item.cid == this.churchid
            )
          else return item.weeks.length === this.totalWeeks && !item.cid
        })
        return hasMission
      },
      // NEW: Available languages for selection
      availableLanguages() {
        return this.enabledLangs.map((lang) => ({
          code: lang.code,
          label: lang.label,
        }))
      },
      // NEW: Filter weeks by selected language
      filteredWeeksByLanguage() {
        if (!this.requiredLanguage) {
          return []
        }
        return this.weeksList.filter((week) =>
          this.hasLocalizedContent(week, this.requiredLanguage)
        )
      },
    },
    watch: {
      churchid: {
        deep: true,
        handler(event) {
          this.getMissions(event)
        },
      },
      requiredLanguage: {
        handler(newLang, oldLang) {
          // If language changes after weeks were selected, clear selections
          if (oldLang && newLang !== oldLang && this.selectedWeeks.length > 0) {
            // Show confirmation dialog
            if (
              confirm(
                'Changing language will clear your selected weeks. Continue?'
              )
            ) {
              this.selectedWeeks = []
              this.selectedWeekIds = []
            } else {
              // Revert the change
              this.$nextTick(() => {
                this.requiredLanguage = oldLang
              })
            }
          }
        },
      },
    },
    methods: {
      // NEW: Check if week has content in specified language
      hasLocalizedContent(week, languageCode) {
        if (week.localizedContent && week.localizedContent[languageCode]) {
          const content = week.localizedContent[languageCode]
          return (
            (content.title && content.title.trim()) ||
            (content.subtitle && content.subtitle.trim()) ||
            (content.missionBodyContent && content.missionBodyContent.trim()) ||
            (content.supplicationBodyContent &&
              content.supplicationBodyContent.trim()) ||
            (content.supplicationStudyQuestions &&
              content.supplicationStudyQuestions.length > 0) ||
            (content.missionStudyQuestions &&
              content.missionStudyQuestions.length > 0) ||
            (content.supplicationVerseQuotes &&
              content.supplicationVerseQuotes.length > 0)
          )
        }
        // Check for legacy structure (top-level fields for EN, week.pt.* for PT)
        if (languageCode === 'en') {
          return week.title && week.title.trim()
        }
        if (languageCode === 'pt' && week.pt) {
          return week.pt.title && week.pt.title.trim()
        }
        return false
      },
      getWeekTitle,
      // NEW: Get language color for badges (matching Weeks admin table)
      getLanguageColor(languageCode) {
        const predefinedColors = {
          en: 'blue',
          pt: 'green',
          es: 'orange',
          fr: 'purple',
          de: 'red',
        }
        return predefinedColors[languageCode] || 'grey'
      },
      // NEW: Get available languages for a specific week
      getAvailableLanguagesForWeek(week) {
        const availableLanguages = []
        const supportedLanguages = this.availableLanguages

        supportedLanguages.forEach((lang) => {
          if (this.hasLocalizedContent(week, lang.code)) {
            availableLanguages.push(lang)
          }
        })

        return availableLanguages
      },
      addNewHandler() {
        // this backdoor is used to trigger the weeksList computed property
        this.backdoor++

        this.missionTitle = ''
        this.selectedWeeks = []
        this.requiredLanguage = null // NEW: Reset language selection
        if (this.editingMission) {
          if (this.$refs.addeditform) this.$refs.addeditform.reset()
          this.openMissionForm = true
        } else {
          this.openMissionForm = !this.openMissionForm
        }
        this.editingMission = false
        // console.log('this.openMissionForm ', this.openMissionForm)
      },
      syncSelectedWeeks() {
        if (!Array.isArray(this.selectedWeekIds)) {
          this.selectedWeekIds = []
        }

        this.selectedWeeks = this.selectedWeekIds
          .map((id) => this.weeks.find((w) => w.id === id))
          .filter(Boolean)
      },
      addMission() {
        if (this.missionTitle) {
          // get the id of each selected week
          let listOfWeeks = Object.keys(this.selectedWeeks).map((key) => {
            return this.selectedWeeks[key]
          })

          let listOfWeekIDs = []

          if (listOfWeeks.length) {
            for (let index = 0; index < listOfWeeks.length; index++) {
              const week = listOfWeeks[index]
              // add new week IDs to the mission week array
              listOfWeekIDs.push({
                id: week.id,
              })
            }
          }

          // console.log('1 ', listOfWeekIDs.length, this.missions)
          // const hasDuplicate = this.missions.filter((item) => {
          //     console.log('1.5 ', item.weeks.length, listOfWeekIDs.length)
          //     if(item.weeks.length == listOfWeekIDs.length)
          //         return item

          //     return false
          // })
          // console.log('2 ', hasDuplicate)

          // filter out any new categories without IDs
          const updatedMissionCategory = this.missionCategory.filter(
            (item) => item.id !== undefined
          )

          // if there was a new category add it to the collection
          if (this.newCategory.length) {
            for (let index = 0; index < this.newCategory.length; index++) {
              const cat = this.newCategory[index]
              db.collection('categories')
                .add({
                  name: cat,
                })
                .then((docRef) => {
                  // add new categories to the mission category array
                  updatedMissionCategory.push({
                    id: docRef.id,
                  })
                })
            }
          }
          // remove the name property from the mission category array before adding it to the collection
          updatedMissionCategory.map(function (item) {
            delete item.name
            return item
          })

          let churchPin =
            this.checkAuthorization(['superAdmin']).length > 0
              ? null
              : this.churchid
          let type =
            this.checkAuthorization(['superAdmin']).length > 0
              ? 'public'
              : 'private'

          db.collection('missions')
            .add({
              cid: churchPin,
              type: type,
              weeks: listOfWeekIDs,
              title: this.missionTitle,
              category: updatedMissionCategory,
              requiredLanguage: this.requiredLanguage || 'en', // NEW: Save required language
            })
            .then(() => {
              // Reset fields
              this.selectedWeeks = []
              this.missionTitle = ''
              this.missionCategory = []
              this.requiredLanguage = null // NEW: Reset language
              this.openMissionForm = false

              // re-init the mission list
              this.getMissions(this.$store.getters['AppData/cid'])
            })
        } else {
          this.feedback = this.$t('missions.feedbackNeedTitle')
        }
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const normalizedPermissions = permissions.includes('superAdmin')
            ? [...permissions, 'helpDesk']
            : permissions

          const hasRole = normalizedPermissions.filter(() =>
            userRoles.some((role) => normalizedPermissions.includes(role))
          )
          return hasRole
        }
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      closebuilder() {
        // this backdoor is used to trigger the weeksList computed property
        this.backdoor++

        this.missionTitle = ''
        if (this.editingMission) {
          if (this.$refs.addeditform) this.$refs.addeditform.reset()
        }
        this.openMissionForm = false
        this.editingMission = false
      },
      async deleteItem(item, collection) {
        if (this.isHelpDesk && this.isHelpDesk()) {
          const key = 'missions.feedbackDeleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete missions.'
          return
        }

        if (item && collection) {
          let title = ''
          if (collection === 'missions') {
            title = item.title
          }

          const confirmed = await confirmAction({
            title: `You are about to delete: <br>"${title}"`,
            html: 'Type DELETE below to confirm.',
            input: 'text',
            inputPlaceholder: 'Type DELETE',
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (matchesConfirmWord(value, 'DELETE')) {
                  resolve()
                } else {
                  resolve('You need to type DELETE to confirm')
                }
              })
            },
            confirmButtonText: 'Yes Delete it!',
            cancelButtonText: 'No, Keep it!',
            context: this,
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })

          if (confirmed) {
            await db.collection(collection).doc(item.id).delete()
            this.getMissions(this.$store.getters['AppData/cid'])
          }
        } else {
          Swal.fire({
            title: 'Houston we have a problem.',
            text: 'Error with the delete function, missing params.',
            icon: 'error',
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }
      },
      editMission() {
        this.iseditingMission = true
        // mission ? mission : ''

        if (this.missionTitle && this.missionToEdit) {
          // filter out any new categories without IDs
          const updatedMissionCategory = this.missionCategory.filter(
            (item) => item.id !== undefined
          )

          // if there was a new category add it to the collection
          if (this.newCategory.length) {
            for (let index = 0; index < this.newCategory.length; index++) {
              const cat = this.newCategory[index]
              db.collection('categories')
                .add({
                  name: cat,
                })
                .then((docRef) => {
                  // add new categories to the mission category array
                  updatedMissionCategory.push({
                    id: docRef.id,
                  })
                })
            }
          }
          // remove the name property from the mission category array before adding it to the collection
          updatedMissionCategory.map(function (item) {
            delete item.name
            return item
          })

          setTimeout(() => {
            // get the id of each selected week
            let listOfWeeks = Object.keys(this.selectedWeeks).map((key) => {
              return this.selectedWeeks[key]
            })

            let listOfWeekIDs = []

            if (listOfWeeks.length) {
              for (let index = 0; index < listOfWeeks.length; index++) {
                const week = listOfWeeks[index]
                // add new week IDs to the mission week array
                listOfWeekIDs.push({
                  id: week.id,
                })
              }
            }

            db.collection('missions')
              .doc(this.missionToEdit.id)
              .update({
                type: this.missionToEdit.type,
                weeks: listOfWeekIDs,
                title: this.missionTitle,
                category: updatedMissionCategory,
                requiredLanguage: this.requiredLanguage || 'en', // NEW: Save required language
              })
              .then(() => {
                // Reset fields
                this.selectedWeeks = []
                this.missionTitle = ''
                this.missionCategory = ''
                this.type = ''
                this.requiredLanguage = null // NEW: Reset language
                this.openMissionForm = false
                this.iseditingMission = false
                this.editingMission = false
                this.missionToEdit = null

                // re-init the mission list
                this.getMissions(this.$store.getters['AppData/cid'])
              })
          }, 1000)
        } else {
          this.feedback = this.$t('missions.feedbackError') + ' '

          if (!this.missionTitle)
            this.feedback += this.$t('missions.feedbackMissingTitle') + ' '
          if (!this.missionToEdit)
            this.feedback += this.$t('missions.feedbackNoMissionToEdit') + ' '
        }
      },
      filterlist(letter, i) {
        this.activeindex = i
        this.filtered = letter.toLowerCase()
      },
      getLeader(id) {
        const leader = this.users.filter((x) => x.uid === id)
        return leader
      },
      loadMissionToEdit(mission) {
        if (
          this.editingMission &&
          this.missionToEdit &&
          this.missionToEdit.title === mission.title
        ) {
          this.openMissionForm = false
          this.editingMission = false
          return
        }

        this.editingMission = true
        this.missionToEdit = mission
        this.missionCategory = []

        // NEW: Set required language from mission (or default to 'en')
        this.requiredLanguage = mission.requiredLanguage || 'en'

        // ✅ Set selected week IDs
        this.selectedWeekIds = mission.weeks.map((w) => w.id)

        // ✅ Clone selected weeks and include locked status
        this.selectedWeeks = mission.weeks
          .map((w) => {
            const found = this.weeks.find((week) => week.id === w.id)
            if (found) {
              return {
                ...found, // shallow clone to avoid Vuex mutation
                locked: w.locked,
              }
            }
            return null
          })
          .filter(Boolean)

        // ✅ Set categories if any
        if (mission.category.length) {
          this.missionCategory = this.allCategories.filter((obj) =>
            mission.category.some((obj2) => obj.id === obj2.id)
          )
        } else {
          this.missionCategory = []
        }

        this.openMissionForm = true
        this.missionTitle = mission.title

        this.scrollToTop()
      },
      lockWeek(week, i) {
        week.locked = !week.locked
        this.$set(this.selectedWeeks, i, week)
      },
      scrollToTop() {
        document
          .getElementById('missions')
          .scrollIntoView({ behavior: 'smooth' })
      },
      weekSelected(u) {
        return this.weeksList.filter((item) => {
          if (item.id === u.id) {
            item.locked = false
          }
        })
      },
      updateCategories() {
        const allCat = this.allCategories
        const secCat = this.missionCategory

        // Find values that are in secCat but not in allCat
        this.newCategory = secCat.filter(function (obj) {
          return !allCat.some(function (obj2) {
            return obj.name == obj2.name
          })
        })
      },
      previewMission(item) {
        this.feedback = ''

        if (item) {
          openPreview(this.$router, this.$route, {
            name: 'PreviewMission',
            id: item.id,
            lang: item.requiredLanguage || 'en',
          })
          return
        }

        if (this.missionTitle && this.selectedWeeks.length > 0) {
          const opened = openPreview(this.$router, this.$route, {
            name: 'PreviewMission',
            lang: this.requiredLanguage || 'en',
            payload: {
              title: this.missionTitle,
              selectedWeeks: this.selectedWeeks,
              requiredLanguage: this.requiredLanguage || 'en',
            },
          })
          if (!opened) {
            this.feedback =
              'Preview could not be opened. Try a saved mission, or free some browser storage.'
          }
          return
        }

        if (!this.missionTitle && this.selectedWeeks.length === 0)
          this.feedback =
            'You need to have a Mission title and Selected Weeks'
        if (!this.missionTitle && this.selectedWeeks.length > 0)
          this.feedback = 'You need to have a Mission title'
        if (this.missionTitle && this.selectedWeeks.length === 0)
          this.feedback = 'You need to have Selected Weeks'
      },
      init() {
        this.isloading = true
        this.openMissionForm = false
        this.editingMission = false

        // we set a timeout for the User experience
        setTimeout(() => {
          this.weeks = []
          let weeksRef = db.collection('weeks')
          weeksRef
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                let week = doc.data()
                week.id = doc.id

                // add all default missions and then only missions with the current church id
                if (!week.cid) this.weeks.push(week)
                else if (week.cid === this.$store.getters['AppData/cid'])
                  this.weeks.push(week)
              })
            })
            .then(() => {
              this.allCategories = []
              let catRef = db.collection('categories')
              catRef.get().then((snapshot) => {
                snapshot.forEach((doc) => {
                  let cat = doc.data()
                  cat.id = doc.id
                  this.allCategories.push(cat)
                })
              })
            })
            .then(() => {
              this.isloading = false
            })
        }, 1000)
      },
      getMissions(cid) {
        // console.log('getMissions ', cid)
        // let missionsRef = db.collection('missions')
        // missionsRef.onSnapshot(snapshot => {
        let missionsRef = db.collection('missions')
        missionsRef.get().then((snapshot) => {
          this.missions = []
          snapshot.forEach((snap) => {
            let mission = snap.data()
            mission.id = snap.id
            // this.missions.push(mission)

            if (cid === null || cid === undefined) {
              if (
                this.isAuthenticated &&
                this.checkAuthorization(['superAdmin']).length > 0
              )
                this.missions.push(mission)
            } else {
              if (
                (this.isAuthenticated &&
                  this.checkAuthorization(['superAdmin', 'admin']).length > 0 &&
                  !mission.cid) ||
                (mission.cid && mission.cid == cid)
              ) {
                this.missions.push(mission)
                this.missions.sort((a) => {
                  if (a.cid) return -1
                  return 0
                })
              }
            }

            if (
              this.isAuthenticated &&
              !this.checkAuthorization(['superAdmin']).length > 0
            ) {
              this.missions.sort((a) => {
                if (a.cid) return -1
                return 0
              })
            }
          })

          this.init()
        })
      },
    },
    created() {
      this.getMissions(this.$store.getters['AppData/cid'])
    },
  }
</script>

<style>
  .missions-container {
    margin-bottom: 25%;
  }
  .missions-container .mission-heading h1 {
    display: flex;
    align-items: center;
  }
  .letter-filter-container {
    display: flex;
    align-items: center;
  }
  .letter-filter-container .clear-filter {
    margin: 0 5px;
  }
  .letter-filter-container ul {
    list-style: none;
    display: flex;
    padding-left: 0px;
  }
  .letter-filter-container ul li {
    margin-right: 5px;
    cursor: pointer;
  }
  .letter-filter-container ul li span:hover {
    font-weight: 900;
  }
  .letter-filter-container ul li span.active {
    font-weight: 900;
  }
  .search-field .v-text-field__details {
    display: none;
  }
  .v-input--is-label-active label {
    font-weight: 900;
  }
  .missions-container
    .v-input:not(.v-input--is-dirty).v-select.v-select--chips:not(
      .v-text-field--single-line
    ).v-text-field--enclosed
    .v-select__selections {
    min-height: inherit;
  }
  .v-autocomplete__content .v-list.v-select-list {
    display: flex;
    flex-wrap: wrap;
  }
  .v-autocomplete__content {
    max-width: 705px;
  }
  .v-autocomplete__content .v-list.v-select-list .v-list-item {
    flex: 1 1 50%;
  }
  .v-list-item--disabled img {
    filter: grayscale(1);
    opacity: 0.15;
  }
  .v-list-item__avatar {
    justify-content: center;
  }
  .missions-container .add-weeks-link {
    cursor: pointer;
  }
  .border-grey {
    border-color: #b5b5b5 !important;
  }
  .selected-weeks .v-list-item__icon {
    align-self: center;
  }
  .selected-weeks .v-list-item__icon {
    cursor: pointer;
  }
  .selected-weeks .v-list-item__prepend .handle {
    cursor: grab;
  }
  .custom-selected-weeks .v-selection-control {
    min-height: unset !important;
  }
</style>
